import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Loading from "../../atoms/loading";
import Button from "../../atoms/button";
import LoadingOverlay from "../../atoms/loadingOverlay";
import ToastContext from "../../../context/toastProvider";
import {
  downloadBlob,
  saveModelsTags,
  saveManualTagEmissions,
} from "../functions";
import { buildApplicableGradePayload } from "../../utils/serialGradeFields";
import { isDouradoEligible } from "./partitionByPaper";
import { buildTagsZip } from "./buildTagsZip";
import { formatLocalDate, zipDownloadName } from "./tagZipNames";
import { focusFirstTagErrorField } from "./focusFirstTagError";
import {
  buildPartitionErrorData,
  buildTagsSendData,
  buildTagSavePayload,
  buildManualAuditPayload,
  applySavedTagsToSerials,
  cardFromSerial,
} from "./submitTagsFlow";
import { validateSelectedTags } from "./validateTagsLive";
import { preloadTagFonts, tagFontsReady } from "./preloadTagFonts";
import TagLabelsWorkspace from "./TagLabelsWorkspace";
import TagsSelectionHub from "./TagsSelectionHub";
import ManualTagsWizard from "./ManualTagsWizard";
import ExtendedArtBlockModal from "./ExtendedArtBlockModal";
import { resolveAndSplit } from "./tagSources";
import { extendedCardFromSerial } from "./extendedCards";
import { isArtLabelsEnabled } from "./artLabelsFlag";
import { enrichCardsWithArt } from "./artEnrich";
import { isArtPngFailure } from "./artPngFetch";
import { getArtLabel } from "../../artLabels/functions";
import { markExtendedArtTagsGenerated } from "../../extendedArt/functions";
import { EXTENDED_ART_KIND } from "../../extendedArt/kind";
import { DEFAULT_GEOMETRY } from "../../artLabels/artLabelPreviewMath";
import "./tagFonts.css";
import { Sentry } from "../../../sentry";

const PRODUCTS_SESSION_KEY = "mgs-tags-products";
const EXTENDED_SESSION_KEY = "mgs-tags-extended-orders";
const TAGS_SESSION_KEY = "mgs-tags-session";

const readSessionList = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch (_err) {
    return [];
  }
};

const writeSessionList = (key, list) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(list || []));
  } catch (_err) {
    /* ignore quota */
  }
};

const clearSessionList = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch (_err) {
    /* ignore: storage indisponível */
  }
};

const writeTagsSession = (payload) => {
  try {
    sessionStorage.setItem(TAGS_SESSION_KEY, JSON.stringify(payload));
  } catch (_err) {
    /* ignore quota */
  }
};

const buildCardsFromSerials = (serialList) =>
  (serialList || []).map((s) => {
    const card = cardFromSerial(
      s,
      buildApplicableGradePayload(s, s?.product || s?.productData)
    );
    if (isDouradoEligible(card)) {
      card.invertida = false;
    }
    return card;
  });

/** Cartões comuns e estendidos (spec §5.4): o estendido leva a condição e a arte do próprio serial. */
const buildAllCards = (serialList, extendedList) => [
  ...buildCardsFromSerials(serialList),
  ...(extendedList || []).map(extendedCardFromSerial),
];

const TagGenerator = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location || {};
  // Navegação (lista de pedidos, detalhe) vence a sessão; sem navegação, retoma a última seleção da aba.
  const fromNav = Boolean(state?.products?.length || state?.extendedOrders?.length);
  const products = fromNav ? state?.products || [] : readSessionList(PRODUCTS_SESSION_KEY);
  const extendedOrders = fromNav ? state?.extendedOrders || [] : readSessionList(EXTENDED_SESSION_KEY);
  const { goodToast, badToast } = useContext(ToastContext);

  const [phase, setPhase] = useState(() => (products.length || extendedOrders.length ? "generator" : "hub"));
  const [serials, setSerials] = useState(null);
  const [extendedSerials, setExtendedSerials] = useState([]);
  const [blockState, setBlockState] = useState(null);
  const [missing, setMissing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generationLoading, setGenerationLoading] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const [tagsData, setTagsData] = useState([]);
  const [selection, setSelection] = useState(false);
  const [fontsReady, setFontsReady] = useState(() => tagFontsReady());
  const [unifyPages, setUnifyPages] = useState(false);
  const [artGeometry, setArtGeometry] = useState(DEFAULT_GEOMETRY);
  // Sequência do último carregamento de cartões: um antigo que termine depois
  // de um novo não pode sobrescrever o resultado mais recente.
  const artSeqRef = useRef(0);
  // Placas por `artKey@version` que sobrevivem ao reenriquecimento: a URL
  // presign da arte expira em 600 s e o reenriquecimento pós-save não
  // consegue mais amostrar a imagem.
  const platesCacheRef = useRef(new Map());

  useEffect(() => {
    if (!fromNav) return;
    writeSessionList(PRODUCTS_SESSION_KEY, state?.products);
    writeSessionList(EXTENDED_SESSION_KEY, state?.extendedOrders);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.products, state?.extendedOrders]);

  useEffect(() => {
    let cancelled = false;
    preloadTagFonts()
      .then(() => {
        if (!cancelled) setFontsReady(true);
      })
      .catch(() => {
        /* ignore: font optional */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (products.length || extendedOrders.length) {
      loadFromSources({ productIds: products, extendedOrderIds: extendedOrders, serialNumbers: [] });
    }
    // eslint-disable-next-line
  }, []);

  /**
   * Monta os cartões (comuns + estendidos) e calcula as placas da arte
   * estendida. `aplicarCartas` roda no mesmo tick em que o cálculo resolve.
   */
  const loadCards = async (serialList, extendedList, aplicarCartas) => {
    const seq = artSeqRef.current + 1;
    artSeqRef.current = seq;
    const { cards, geometry } = await enrichCardsWithArt({
      cards: buildAllCards(serialList, extendedList),
      token: user?.token,
      cache: platesCacheRef.current,
    });
    if (artSeqRef.current !== seq) return null;
    setArtGeometry(geometry);
    if (aplicarCartas) aplicarCartas(cards);
    return cards;
  };

  useEffect(() => {
    if (phase !== "generator" || (!serials?.length && !extendedSerials.length)) return undefined;
    let cancelled = false;
    loadCards(serials, extendedSerials, (cards) => {
      if (cancelled) return;
      setTagsData((prev) => {
        const manuals = (prev || []).filter((c) => c?.source === "manual");
        const manualSerials = new Set(manuals.map((m) => m.serial));
        return [...cards.filter((c) => !manualSerials.has(c.serial)), ...manuals];
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serials, extendedSerials]);

  // Validação em tempo real (largura + partição) antes do download.
  useEffect(() => {
    if (!tagsData?.length) {
      setErrorData(null);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      try {
        const next = validateSelectedTags(tagsData);
        setErrorData(next.cards.length ? next : null);
      } catch (error) {
        const partitionError = buildPartitionErrorData(error);
        setErrorData(partitionError);
      }
    }, 200);
    return () => window.clearTimeout(timer);
  }, [tagsData, fontsReady]);

  const applySources = ({ serials: common, extendedSerials: allowed, missing: nextMissing, productIds = [], serialNumbers = [] }) => {
    setSerials(common);
    setExtendedSerials(allowed);
    setMissing(nextMissing);
    const nextPhase = nextMissing.length ? "wizard" : "generator";
    writeTagsSession({ productIds, serialNumbers, serials: common, extendedSerials: allowed, phase: nextPhase });
    setPhase(nextPhase);
  };

  /** Resolve a seleção nas duas fontes e separa os estendidos bloqueados (spec §5.4). */
  const loadFromSources = async ({ productIds = [], extendedOrderIds = [], serialNumbers = [] }) => {
    setLoading(true);
    let sources;
    try {
      sources = await resolveAndSplit(
        { productIds, extendedOrderIds, serialNumbers },
        { token: user?.token, artEnabled: isArtLabelsEnabled(), mobile: false }
      );
    } catch (error) {
      badToast(error.message);
      setLoading(false);
      return;
    }
    setLoading(false);
    const { blocked } = sources;
    const next = { serials: sources.serials, extendedSerials: sources.allowedExtended, missing: sources.missing, productIds, serialNumbers };
    if (blocked.length) {
      setBlockState({ blocked, next });
      return;
    }
    applySources(next);
  };

  const handleContinue = (picked) => loadFromSources(picked);

  const buildCardForPartition = (serial, tagsEntry) => {
    if (tagsEntry) return tagsEntry;
    return {
      ...buildApplicableGradePayload(serial, serial?.product || serial?.productData),
      serial: serial?.serialNumber,
    };
  };

  const handleWizardComplete = async (manualCards) => {
    if (!serials?.length && !extendedSerials.length && !manualCards?.length) {
      badToast("Nenhum serial selecionado");
      setPhase("hub");
      setSerials(null);
      setExtendedSerials([]);
      setMissing([]);
      setTagsData([]);
      return;
    }
    // `nextTagsData` só é preenchido se este carregamento ainda for o atual.
    let nextTagsData = null;
    await loadCards(serials, extendedSerials, (cards) => {
      nextTagsData = [...cards, ...(manualCards || [])];
      setTagsData(nextTagsData);
    });
    setPhase("generator");
    if (nextTagsData) {
      writeTagsSession({
        serials,
        extendedSerials,
        phase: "generator",
        tagsData: nextTagsData,
      });
    }
  };

  const handleWizardCancel = () => {
    setPhase("hub");
    setMissing([]);
  };

  const selectedCount = tagsData?.filter((d) => d?.select)?.length || 0;
  const allSelected =
    tagsData?.length > 0 && selectedCount === tagsData.length;
  const hasBlockingErrors = (errorData?.cards?.length || 0) > 0;
  // Referência estável: o `serialByNumber` do TagLabelsWorkspace memoiza por ela.
  const workspaceSerials = useMemo(
    () => [...(serials || []), ...extendedSerials],
    [serials, extendedSerials]
  );
  const canDownload = selectedCount > 0 && !hasBlockingErrors;

  const setAllSelect = (value) => {
    setTagsData((prev) => (prev || []).map((card) => ({ ...card, select: value })));
  };

  const enableSelection = () => {
    setAllSelect(false);
    setSelection(true);
  };

  const cancelSelection = () => {
    setAllSelect(true);
    setSelection(false);
  };

  const toggleSelectAll = () => {
    setAllSelect(!allSelected);
  };

  /**
   * A URL presigned da arte vale 600 s e foi assinada ao abrir a tela: numa
   * sessão de edição longa ela já expirou aqui. Reassina no clique de gerar,
   * uma vez por artKey (o cache fica no composeArtSheet).
   */
  const resolveArtUrl = async (artKey) => {
    const out = await getArtLabel(user?.token, artKey);
    return out?.type === "success" ? out.data?.approved?.url || null : null;
  };

  // Spec 2026-10-01 P8: grava tagGeneratedAt nos seriais estendidos que saíram no ZIP.
  // Falha aqui não desfaz o download; só avisa.
  const markExtendedTagsGenerated = async (sentCards) => {
    const sent = new Set(sentCards.filter((card) => card?.kind === EXTENDED_ART_KIND).map((card) => card.serial));
    const ids = extendedSerials.filter((serial) => sent.has(serial.serialNumber)).map((serial) => serial._id);
    if (!ids.length) return;
    const out = await markExtendedArtTagsGenerated(user?.token, ids);
    if (out?.type !== "success") {
      badToast("Etiquetas baixadas, mas não foi possível registrar a data da etiqueta dos itens de arte estendida.");
    }
  };

  const submitTags = async () => {
    try {
      setGenerationLoading(true);
      const liveErrors = validateSelectedTags(tagsData);
      if (liveErrors.cards.length) {
        setErrorData(liveErrors);
        badToast("Corrija os campos que excedem o limite antes de baixar");
        window.setTimeout(() => focusFirstTagErrorField(liveErrors), 80);
        return;
      }
      setErrorData(null);
      const sendData = buildTagsSendData(tagsData);
      const savePayload = buildTagSavePayload(tagsData, serials);

      if (savePayload.length) {
        const response = await saveModelsTags(user?.token, savePayload);
        if (response?.type === "error") {
          if (response?.data) {
            setErrorData(response.data);
          }
          badToast(response?.message || "Erro ao salvar etiquetas");
          return;
        }
        setSerials((prev) => applySavedTagsToSerials(prev, savePayload));
      }

      const auditPayload = buildManualAuditPayload(tagsData);
      if (auditPayload.length) {
        const manualResponse = await saveManualTagEmissions(user?.token, auditPayload);
        if (manualResponse?.type === "error") {
          badToast(manualResponse?.message || "Erro ao registrar etiquetas manuais");
          return;
        }
      }

      const date = formatLocalDate();
      const { blob } = await buildTagsZip(sendData, {
        unifyPages,
        date,
        geometry: artGeometry,
        resolveArtUrl,
      });
      downloadBlob(blob, zipDownloadName(date));
      goodToast("Etiquetas geradas e salvas!");
      // Depois do goodToast: o provider tem uma mensagem só, e o aviso de falha precisa prevalecer.
      await markExtendedTagsGenerated(sendData);
    } catch (error) {
      Sentry.captureException(error, { extra: { area: "tags", action: "submitTags" } });
      if (isArtPngFailure(error)) {
        badToast("Arte indisponível para o ZIP — clique em Gerar de novo");
        return;
      }
      const partitionError = buildPartitionErrorData(error);
      if (partitionError) {
        setErrorData(partitionError);
        badToast(error?.message || "Erro na geração de etiquetas");
        return;
      }
      badToast(error?.message || "Erro na geração de etiquetas");
    } finally {
      setGenerationLoading(false);
    }
  };

  const blockModal = blockState ? (
    <ExtendedArtBlockModal
      blocked={blockState.blocked}
      remainingCount={blockState.next.serials.length + blockState.next.extendedSerials.length + blockState.next.missing.length}
      onReview={(artKey) => navigate(`/manafix/art-labels/${encodeURIComponent(artKey)}`)}
      onQueue={() => navigate("/manafix/art-labels", { state: { tab: "queue" } })}
      onContinue={() => {
        const { next } = blockState;
        setBlockState(null);
        applySources(next);
      }}
      onCancel={() => {
        setBlockState(null);
        // Recarregar a tela não pode reabrir a mesma seleção bloqueada: limpa a
        // sessão e o `location.state` da navegação (que sobrevive ao reload).
        clearSessionList(PRODUCTS_SESSION_KEY);
        clearSessionList(EXTENDED_SESSION_KEY);
        if (fromNav) navigate(location.pathname, { replace: true, state: null });
        setPhase("hub");
      }}
    />
  ) : null;

  if (phase === "hub") {
    return (
      <main className="mb-10 relative pb-24">
        <h1 className="text-lg text-center py-2 border-b border-primary">Gerador de etiquetas</h1>
        <div className="px-2 mt-4">
          <TagsSelectionHub user={user} onContinue={handleContinue} />
        </div>
        {loading ? (
          <div className="w-fit m-auto mt-4">
            <Loading />
          </div>
        ) : null}
        {blockModal}
        <LoadingOverlay loading={loading} />
      </main>
    );
  }

  if (phase === "wizard") {
    return (
      <main className="mb-10 relative pb-24">
        <h1 className="text-lg text-center py-2 border-b border-primary">Gerador de etiquetas</h1>
        <div className="px-2 mt-4">
          <ManualTagsWizard
            missing={missing}
            onComplete={handleWizardComplete}
            onCancel={handleWizardCancel}
            fontsReady={fontsReady}
          />
        </div>
        {blockModal}
        <LoadingOverlay loading={loading} />
      </main>
    );
  }

  return (
    <main className="mb-10 relative pb-24">
      <h1 className="text-lg text-center py-2 border-b border-primary">Gerador de etiquetas</h1>
      {loading ? (
        <div className="w-fit m-auto mt-4">
          <Loading />
        </div>
      ) : (
        <div className="px-2">
          {selection ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button classes="bg-primary m-0" click={cancelSelection}>
                Cancelar seleção
              </Button>
              <Button classes="bg-primary m-0" click={toggleSelectAll}>
                {allSelected ? "Limpar seleção" : "Selecionar todas"}
              </Button>
            </div>
          ) : (
            <div className="mt-2">
              <Button classes="bg-primary" click={enableSelection}>
                Habilitar seleção
              </Button>
            </div>
          )}
          {!serials?.length && !extendedSerials.length && !tagsData?.length ? (
            <p className="text-center">Nenhum serial registrado neste pedido</p>
          ) : (
            <TagLabelsWorkspace
              tagsData={tagsData}
              errorData={errorData}
              serials={workspaceSerials}
              selection={selection}
              setTagsData={setTagsData}
              fontsReady={fontsReady}
              artGeometry={artGeometry}
              douradoEligibleFor={(s, tagsEntry) =>
                isDouradoEligible(buildCardForPartition(s, tagsEntry))
              }
            />
          )}
          {tagsData?.length ? (
            <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#d59106]/50 bg-[#1a1a1a]/95 backdrop-blur-sm px-3 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.35)]">
              <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-3">
                <Button
                  classes="bg-green-300 hover:bg-green-500 text-xl disabled:opacity-50"
                  disabled={!canDownload}
                  click={submitTags}
                >
                  Baixar ZIP
                </Button>
                <label className="flex items-center gap-2 text-sm text-[#ffedd5]/80 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={unifyPages}
                    onChange={(e) => setUnifyPages(e.target.checked)}
                  />
                  Unificar páginas
                </label>
                <span className="text-sm text-[#ffedd5]/80">
                  {selectedCount} selecionada{selectedCount === 1 ? "" : "s"}
                </span>
                {hasBlockingErrors ? (
                  <span className="text-sm text-red-400">
                    Download bloqueado — corrija os campos com borda vermelha.
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      )}
      {blockModal}
      <LoadingOverlay loading={generationLoading} />
    </main>
  );
};

export default TagGenerator;
